import axios from 'axios';
import WooCommerceRestApi from '@woocommerce/woocommerce-rest-api';
import { getCredentials, getConfig } from './woo';
import { searchCustomers as unifiedSearchCustomers } from './unifiedCustomersSearch';
import { formatChileDate } from '../utils/dateUtils';

/**
 * Servicio para gestión de pagos y billing
 * 
 * ARQUITECTURA DE BÚSQUEDA OPTIMIZADA:
 * 
 * 1. SIN TÉRMINO DE BÚSQUEDA:
 *    - Usa WooCommerce REST API estándar (/wc/v3/orders)
 *    - Filtros: status, dates, customer_id
 *    - Post-procesamiento: min_amount, max_amount, group
 * 
 * 2. CON TÉRMINO DE BÚSQUEDA:
 *    - Usa endpoint personalizado (/datofy/v1/orders/search)
 *    - Búsqueda SQL optimizada en:
 *      * Nombres de customer (first_name, last_name)
 *      * Email de customer  
 *      * group_name del customer
 *      * suscription_name del customer
 *      * customer_group_name en meta_data de orden
 *      * grupo en meta_data de orden (fallback)
 *    - Filtros backend: status, dates, group
 *    - Sin post-procesamiento necesario
 * 
 * RENDIMIENTO:
 * - Endpoint optimizado: ~100x más rápido para búsquedas
 * - Escalable para miles de órdenes
 * - Paginación nativa en SQL
 */

// Crear instancia de WooCommerce API para pagos (usar mismo patrón que woo.js)
function createWooCommerceInstance() {
  const credentials = getCredentials();

  return new WooCommerceRestApi({
    url: getConfig(credentials, "WOO_API_URL") || "https://pagos.blaspascal.cl",
    consumerKey: getConfig(credentials, "WOO_API_CONSUMER_KEY") || 'ck_079f5433b516c199daafcd58acc313419c58445e',
    consumerSecret: getConfig(credentials, "WOO_API_CONSUMER_SECRET") || 'cs_00f1b1ea89a8824919870a10c8543271ad71002e',
    version: 'wc/v3',
    queryStringAuth: true,
    axiosConfig: {
      transformRequest: [(data, headers) => {
        // Eliminar el User-Agent de los headers
        delete headers['User-Agent'];
        return data;
      }]
    }
  });
}

// Crear instancia para endpoints personalizados de WordPress
export function createWordPressAPIInstance() {
  const credentials = getCredentials();
  const baseURL = (getConfig(credentials, "WOO_API_URL") || 'https://pagos.blaspascal.cl') + '/wp-json/datofy/v1';
  
  console.log('🔧 BILLING: WordPress API configurado con URL:', baseURL);
  
  return axios.create({
    baseURL: baseURL,
    headers: {
      'Content-Type': 'application/json'
    },
    timeout: 30000 // 30 segundos timeout
  });
}

/**
 * Obtener pagos con filtros avanzados
 * 
 * IMPLEMENTA BÚSQUEDA OPTIMIZADA:
 * 1. Si hay término de búsqueda: Usa endpoint personalizado /datofy/v1/orders/search
 * 2. Si no hay búsqueda: Usa WooCommerce API estándar
 * 3. Para exportación: Itera todas las páginas del endpoint correspondiente
 * 
 * @param {Object} filters - Filtros aplicados (incluyendo search para nombres)
 * @param {Object} pagination - Configuración de paginación (usar per_page: -1 para obtener todos)
 * @returns {Promise} Datos de pagos filtrados con búsqueda optimizada
 */
export const getPaymentsWithFilters = async (filters = {}, pagination = { page: 1, per_page: 50 }) => {
  try {
    console.log('getPaymentsWithFilters - filters:', filters, 'pagination:', pagination);
    
    // SIEMPRE usar el endpoint optimizado (que ahora filtra por date_modified)
    const wpApi = createWordPressAPIInstance();
    
    // Para exportación completa, usar per_page alto o múltiples llamadas
    const isExportMode = pagination.per_page === -1 || pagination.per_page > 1000;
    
    if (isExportMode) {
      return await getAllPaymentsWithOptimizedSearch(filters);
    } else {
      return await getPagedPaymentsWithOptimizedSearch(filters, pagination);
    }

  } catch (error) {
    console.error('Error fetching payments with filters:', error);
    throw error;
  }
};

/**
 * Obtener una página específica de pagos
 */
const getPagedPaymentsWithFilters = async (api, filters, pagination) => {
  // Construir parámetros de consulta (siguiendo patrón de getOrders en woo.js)
  const params = {
    page: pagination.page,
    per_page: Math.min(pagination.per_page, 100), // WooCommerce máximo 100 por página
    orderby: 'date',
    order: 'desc'
  };

  // Aplicar filtros
  applyFiltersToParams(params, filters);

  console.log('Billing API params (paged):', params); // Debug

  // Llamar a la API directamente con parámetros
  const response = await api.get('orders', params);

  console.log('Billing API response (paged):', response.data?.length || 0, 'orders'); // Debug

  // Extraer datos de la respuesta
  let orders = Array.isArray(response.data) ? response.data : [];
  
  // Aplicar filtros de post-procesamiento
  orders = applyPostProcessingFilters(orders, filters);

  // Extraer headers de paginación
  const total = parseInt(response.headers?.['x-wp-total'] || response.headers?.['X-WP-Total'] || orders.length);
  const totalPages = parseInt(response.headers?.['x-wp-totalpages'] || response.headers?.['X-WP-TotalPages'] || 1);

  return {
    data: orders,
    total: total || 0,
    total_pages: totalPages || 1,
    current_page: pagination.page
  };
};

/**
 * Obtener todos los pagos iterando páginas (para exportación)
 */
const getAllPaymentsWithFilters = async (api, filters) => {
  const allOrders = [];
  let page = 1;
  let hasMoreData = true;
  let totalFromHeaders = 0;

  console.log('Starting export mode - fetching all orders'); // Debug

  while (hasMoreData) {
    const params = {
      page: page,
      per_page: 100, // Máximo por página
      orderby: 'date',
      order: 'desc'
    };

    // Aplicar filtros
    applyFiltersToParams(params, filters);

    try {
      const response = await api.get('orders', params);
      const orders = Array.isArray(response.data) ? response.data : [];
      
      if (orders.length === 0) {
        hasMoreData = false;
        break;
      }

      // Aplicar filtros de post-procesamiento
      const filteredOrders = applyPostProcessingFilters(orders, filters);
      allOrders.push(...filteredOrders);

      // Obtener total de headers solo en la primera página
      if (page === 1) {
        totalFromHeaders = parseInt(response.headers?.['x-wp-total'] || response.headers?.['X-WP-Total'] || 0);
      }

      // Verificar si hay más páginas
      const totalPages = parseInt(response.headers?.['x-wp-totalpages'] || response.headers?.['X-WP-TotalPages'] || 1);
      hasMoreData = page < totalPages;
      page++;

      console.log(`Export: fetched page ${page - 1}, got ${filteredOrders.length} orders, total so far: ${allOrders.length}`); // Debug

    } catch (error) {
      console.error(`Error fetching page ${page}:`, error);
      hasMoreData = false;
    }
  }

  console.log(`Export complete: ${allOrders.length} total orders fetched`); // Debug

  return {
    data: allOrders,
    total: allOrders.length,
    total_pages: 1,
    current_page: 1
  };
};

/**
 * FUNCIONES PARA ENDPOINT OPTIMIZADO DE BÚSQUEDA
 */

/**
 * Búsqueda optimizada con paginación usando endpoint personalizado
 */
const getPagedPaymentsWithOptimizedSearch = async (filters, pagination) => {
  const wpApi = createWordPressAPIInstance();
  
  // Construir parámetros para el endpoint optimizado
  const params = {
    page: pagination.page,
    per_page: Math.min(pagination.per_page, 100),
    orderby: filters.orderby || 'date_modified',
    order: filters.order || 'desc'
  };

  // Aplicar filtros normales
  params.customer_id = filters.customer_id || '';
  params.status = filters.status !== 'all' ? filters.status : '';
  params.start_date = filters.start_date || '';
  params.end_date = filters.end_date || '';
  params.group = filters.group || '';

  console.log('🔍 BILLING: Enviando parámetros al endpoint:', params);
  console.log('🔍 BILLING: Filtros originales:', filters);
  console.log('🔍 BILLING: Paginación recibida:', {
    page: pagination.page,
    per_page: pagination.per_page,
    pagination_object: pagination
  });
  console.log('BILLING DEBUG: customer_id received:', {
    from_filters: filters.customer_id,
    final_param: params.customer_id,
    has_customer_filter: !!params.customer_id
  });

  // Llamar al endpoint personalizado
  const response = await wpApi.get('/orders/search', { params });

  console.log('🔍 BILLING: Respuesta del endpoint:', {
    total: response.data.total,
    total_pages: response.data.total_pages,
    current_page: response.data.current_page,
    data_count: response.data.data?.length || 0,
    params_sent: {
      page: params.page,
      per_page: params.per_page
    },
    customer_id_used: params.customer_id,
    full_response: response.data
  });

  // El endpoint ya retorna en el formato correcto
  return {
    data: response.data.data || [],
    total: response.data.total || 0,
    total_pages: response.data.total_pages || 1,
    current_page: response.data.current_page || pagination.page
  };
};

/**
 * Exportación completa con búsqueda optimizada
 */
const getAllPaymentsWithOptimizedSearch = async (filters) => {
  const wpApi = createWordPressAPIInstance();
  const allOrders = [];
  let page = 1;
  let hasMoreData = true;
  let totalFromResponse = 0;

  console.log('Starting optimized export with customer_id:', filters.customer_id);

  while (hasMoreData) {
    const params = {
      page: page,
      per_page: 100,
      customer_id: filters.customer_id || '',
      status: filters.status !== 'all' ? filters.status : '',
      start_date: filters.start_date || '',
      end_date: filters.end_date || '',
      group: filters.group || '',
      orderby: filters.orderby || 'date',
      order: filters.order || 'desc'
    };

    try {
      const response = await wpApi.get('/orders/search', { params });
      const orders = response.data.data || [];
      
      if (orders.length === 0) {
        hasMoreData = false;
        break;
      }

      allOrders.push(...orders);

      // Obtener total de la primera página
      if (page === 1) {
        totalFromResponse = response.data.total || 0;
      }

      // Verificar si hay más páginas
      const totalPages = response.data.total_pages || 1;
      hasMoreData = page < totalPages;
      page++;

      console.log(`Optimized export: fetched page ${page - 1}, got ${orders.length} orders, total so far: ${allOrders.length}`);

    } catch (error) {
      console.error(`Error fetching optimized page ${page}:`, error);
      hasMoreData = false;
    }
  }

  console.log(`Optimized export complete: ${allOrders.length} total orders fetched`);

  return {
    data: allOrders,
    total: allOrders.length,
    total_pages: 1,
    current_page: 1
  };
};

/**
 * Aplicar filtros a parámetros de API de WooCommerce
 * 
 * LIMITACIONES DE WOOCOMMERCE REST API:
 * - El parámetro 'search' NO busca en billing/shipping names
 * - Solo busca en campos limitados como order ID, order number
 * - La búsqueda por nombres de cliente requiere post-procesamiento local
 * 
 * SOLUCIÓN IMPLEMENTADA:
 * - Para búsquedas: Usar endpoint personalizado /datofy/v1/orders/search
 * - Para filtros sin búsqueda: Usar WooCommerce API estándar
 */
const applyFiltersToParams = (params, filters) => {
  // Aplicar filtros de fecha (WooCommerce acepta estos parámetros)
  if (filters.start_date) {
    params.after = filters.start_date + 'T00:00:00';
  }
  if (filters.end_date) {
    params.before = filters.end_date + 'T23:59:59';
  }

  // Aplicar filtro de estado (puede ser array o string)
  if (filters.status && filters.status !== 'all') {
    params.status = filters.status;
  }

  // Aplicar filtro de cliente
  if (filters.customer_id) {
    params.customer = filters.customer_id;
  }

  // Aplicar filtro de búsqueda - Solo para WooCommerce API (cuando no hay término de búsqueda específico)
  // NOTA: El parámetro 'search' de WooCommerce NO busca en billing/shipping names
  // Para búsquedas por nombres usamos el endpoint personalizado /datofy/v1/orders/search
  if (filters.search) {
    console.log('Aplicando filtro search limitado de WooCommerce:', filters.search);
    params.search = filters.search;
  }
};

/**
 * Aplicar filtros que requieren post-procesamiento
 * Solo para filtros que no se pueden hacer en el backend
 */
const applyPostProcessingFilters = (orders, filters) => {
  let filteredOrders = [...orders];

  // Filtrar por monto si se especifica (solo usado para WooCommerce API, no para endpoint optimizado)
  if (filters.min_amount || filters.max_amount) {
    filteredOrders = filteredOrders.filter(order => {
      const amount = parseFloat(order.total);
      const min = filters.min_amount ? parseFloat(filters.min_amount) : 0;
      const max = filters.max_amount ? parseFloat(filters.max_amount) : Infinity;
      return amount >= min && amount <= max;
    });
  }

  // Filtrar por grupo si se especifica (solo para WooCommerce API, endpoint optimizado ya lo maneja)
  if (filters.group) {
    filteredOrders = filteredOrders.filter(order => {
      const grupoMeta = order.meta_data?.find(meta => meta.key === 'customer_group_name') ||
                       order.meta_data?.find(meta => meta.key === 'grupo');
      return grupoMeta && grupoMeta.value && grupoMeta.value.includes(filters.group);
    });
  }

  // NOTA: La búsqueda por nombres ahora se maneja en el endpoint optimizado /datofy/v1/orders/search
  // Ya no necesitamos post-procesamiento local para búsquedas

  return filteredOrders;
};

/**
 * Obtener clientes con pagos atrasados (optimizado)
 * @param {number} daysOverdue - Días de atraso mínimo
 * @returns {Promise} Lista de clientes con pagos vencidos
 */
export const getOverduePayments = async (daysOverdue = 30) => {
  console.log('🔍 OVERDUE: Iniciando búsqueda de pagos atrasados basado en fecha_vencimiento, días mínimo:', daysOverdue);
  
  try {
    // USAR ENDPOINT OPTIMIZADO DEL PLUGIN (corregido para 2025)
    const wpApi = createWordPressAPIInstance();
    
    console.log('🚀 OVERDUE 2025: Usando endpoint optimizado /orders/overdue');
    console.log(`📋 Parámetros: días_atraso=${daysOverdue}, año=2025`);
    
    // Timeout más largo para evitar errores 504
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 segundos
    
    try {
      // Llamar al endpoint optimizado con retry
      const response = await wpApi.get('/orders/overdue', {
        params: {
          days: daysOverdue,
          limit: 50 // Límite reducido para mejor rendimiento
        },
        signal: controller.signal,
        timeout: 25000
      });
      
      clearTimeout(timeoutId);
      
      const responseData = response.data || {};
      
      console.log('📊 OVERDUE 2025: Respuesta del endpoint:', {
        success: !responseData.error,
        total: responseData.total || 0,
        total_amount: responseData.total_amount || 0,
        data_count: responseData.data?.length || 0,
        has_error: !!responseData.error,
        target_year: responseData.criteria?.target_year,
        orders_found: responseData.criteria?.orders_found,
        orders_processed: responseData.criteria?.orders_processed
      });
      
      // Si hay error específico en el endpoint, lanzar excepción para usar fallback
      if (responseData.error) {
        throw new Error(`Plugin error: ${responseData.error} (línea: ${responseData.error_line || 'N/A'})`);
      }
      
      // Validar que tenemos datos válidos
      if (!Array.isArray(responseData.data)) {
        throw new Error('El endpoint no devolvió un array válido de datos');
      }
      
      // Datos ya procesados por el endpoint optimizado
      const overduePayments = responseData.data;
      
      // Log detallado de los primeros resultados
      if (overduePayments.length > 0) {
        console.log('✅ OVERDUE 2025: Primeros resultados:', {
          sample: overduePayments.slice(0, 2).map(p => ({
            id: p.id,
            customer: p.customer_name,
            days: p.days_overdue,
            amount: p.amount_due,
            year_vencimiento: p.year_vencimiento
          }))
        });
        
        // Verificar que los datos son del año 2025
        const year2025Count = overduePayments.filter(p => p.year_vencimiento === 2025).length;
        console.log(`🎯 OVERDUE 2025: ${year2025Count}/${overduePayments.length} pagos son del año 2025`);
      } else {
        console.log('ℹ️ OVERDUE 2025: No se encontraron pagos atrasados para el año 2025');
      }
      
      console.log('✅ OVERDUE 2025: Proceso completado exitosamente');
      
      return {
        data: overduePayments,
        total: responseData.total || 0,
        total_amount: responseData.total_amount || 0,
        criteria: {
          ...responseData.criteria,
          year_filter: 2025,
          service_used: 'endpoint_optimizado'
        },
        generated_at: responseData.generated_at || new Date().toISOString()
      };
      
    } catch (timeoutError) {
      clearTimeout(timeoutId);
      throw timeoutError;
    }

  } catch (error) {
    console.error('❌ OVERDUE 2025: Error con endpoint optimizado:', {
      message: error.message,
      name: error.name,
      stack: error.stack?.split('\n')[0] // Solo primera línea del stack
    });
    
    // Determinar si es un error de timeout o de conexión
    const isTimeoutError = error.name === 'AbortError' || error.message.includes('timeout');
    const isConnectionError = error.code === 'NETWORK_ERROR' || error.message.includes('Network Error');
    
    if (isTimeoutError) {
      console.warn('⏱️ OVERDUE 2025: Timeout en endpoint, probablemente muchas órdenes');
    } else if (isConnectionError) {
      console.warn('🌐 OVERDUE 2025: Error de conexión con el servidor');
    }
    
    // Intentar fallback con límite menor si fue timeout
    console.log('🔄 OVERDUE 2025: Intentando fallback...');
    return await getOverduePaymentsFallback(daysOverdue);
  }
};

/**
 * Fallback para pagos atrasados usando método anterior (mejorado)
 * @param {number} daysOverdue - Días de atraso mínimo
 * @returns {Promise} Lista de clientes con pagos vencidos
 */
const getOverduePaymentsFallback = async (daysOverdue = 30) => {
  try {
    console.log('🔄 OVERDUE FALLBACK 2025: Usando WooCommerce REST API como fallback');
    
    const api = createWooCommerceInstance();
    
    // Forzar filtro para año 2025 (igual que el endpoint principal)
    const targetYear = 2025;
    const now = new Date();
    
    // Fecha de inicio del año 2025
    const yearStart = `${targetYear}-01-01T00:00:00`;
    const yearEnd = `${targetYear}-12-31T23:59:59`;
    
    const params = {
      status: 'pending',
      after: yearStart,
      before: yearEnd, // Todas las órdenes del 2025
      per_page: 50, // Límite reducido para mejor rendimiento
      orderby: 'date',
      order: 'asc'
    };

    console.log('🔄 OVERDUE FALLBACK 2025: Parámetros:', { 
      ...params, 
      daysOverdue, 
      targetYear,
      yearStart,
      yearEnd 
    });

    const response = await api.get('orders', params);
    const orders = Array.isArray(response.data) ? response.data : [];
    
    console.log('🔄 OVERDUE FALLBACK 2025: Órdenes obtenidas:', orders.length);
    
    // Procesar datos usando fecha_vencimiento con validación de año 2025
    const overduePayments = orders
      .map(order => {
        // Buscar fecha_vencimiento en meta_data
        const fechaVencimientoMeta = order.meta_data?.find(meta => meta.key === 'fecha_vencimiento');
        const fechaVencimiento = fechaVencimientoMeta?.value;
        
        let daysLate = 0;
        let calculadoDesde = 'date_created';
        let yearVencimiento = null;
        
        if (fechaVencimiento) {
          // Usar fecha_vencimiento como criterio real de morosidad
          const vencimientoDate = new Date(fechaVencimiento);
          if (!isNaN(vencimientoDate.getTime())) {
            yearVencimiento = vencimientoDate.getFullYear();
            
            // Solo procesar si la fecha de vencimiento es del año objetivo
            if (yearVencimiento !== targetYear) {
              return null; // Excluir órdenes que no sean del año 2025
            }
            
            daysLate = Math.floor((now - vencimientoDate) / (1000 * 60 * 60 * 24));
            calculadoDesde = 'fecha_vencimiento';
          } else {
            return null; // Excluir si fecha_vencimiento es inválida
          }
        } else {
          return null; // Excluir órdenes sin fecha_vencimiento en el fallback
        }
        
        const customerInfo = order.customer_info || {};
        const billing = order.billing || {};
        
        return {
          id: order.id,
          order_key: order.order_key,
          status: order.status,
          date_created: order.date_created,
          fecha_vencimiento: fechaVencimiento,
          days_overdue: daysLate,
          vencimiento_calculado_desde: calculadoDesde,
          customer_id: order.customer_id || 0,
          customer_name: customerInfo.first_name && customerInfo.last_name 
            ? `${customerInfo.first_name} ${customerInfo.last_name}`.trim()
            : customerInfo.username || 
              `${billing.first_name || ''} ${billing.last_name || ''}`.trim() || 'Sin nombre',
          customer_email: customerInfo.email || billing.email || 'Sin email',
          amount_due: parseFloat(order.total || 0),
          currency: order.currency || 'CLP',
          concept: order.meta_data?.find(meta => meta.key === 'concepto')?.value || 'Mensualidad',
          grupo: order.meta_data?.find(meta => meta.key === 'customer_group_name')?.value || 
                 order.meta_data?.find(meta => meta.key === 'grupo')?.value || 'Sin grupo',
          payment_method: order.payment_method_title || '',
          year_vencimiento: yearVencimiento
        };
      })
      .filter(payment => payment !== null && payment.days_overdue >= daysOverdue)
      .sort((a, b) => b.days_overdue - a.days_overdue);

    console.log(`🔄 OVERDUE FALLBACK 2025: Procesados ${overduePayments.length} pagos con ${daysOverdue}+ días desde fecha_vencimiento del año ${targetYear}`);
    
    // Log de debug sobre cálculos
    const year2025Count = overduePayments.filter(p => p.year_vencimiento === 2025).length;
    console.log(`🎯 OVERDUE FALLBACK 2025: ${year2025Count}/${overduePayments.length} pagos son del año 2025`);

    return {
      data: overduePayments,
      total: overduePayments.length,
      total_amount: overduePayments.reduce((sum, p) => sum + p.amount_due, 0),
      criteria: {
        target_year: targetYear,
        days_overdue_min: daysOverdue,
        service_used: 'fallback_woocommerce_api'
      },
      fallback_used: true
    };

  } catch (fallbackError) {
    console.error('❌ OVERDUE FALLBACK: También falló:', fallbackError.message);
    
    // Retornar estructura vacía como último recurso
    return {
      data: [],
      total: 0,
      total_amount: 0,
      error: 'No se pudieron obtener pagos atrasados',
      error_details: fallbackError.message
    };
  }
};

/**
 * Obtener estadísticas de pagos para un rango de fechas
 * @param {Object} dateRange - Rango de fechas
 * @returns {Promise} Estadísticas de pagos
 */
export const getPaymentStats = async (dateRange = {}) => {
  try {
    const wpApi = createWordPressAPIInstance();
    
    // Usar fechas del año actual por defecto
    const currentYear = new Date().getFullYear();
    const start_date = dateRange.start_date || `${currentYear}-01-01`;
    const end_date = dateRange.end_date || `${currentYear}-12-31`;

    console.log('🔍 BILLING: Getting payment stats for:', { start_date, end_date });

    // Usar endpoints del plugin para obtener totales reales correctos
    try {
      console.log('🚀 BILLING: Llamando a endpoints del plugin...');
      
      // Obtener totales por estado usando endpoints optimizados
      const [completedSummaryResponse, pendingSummaryResponse] = await Promise.all([
        wpApi.get(`/orders/summary?status=completed&start_date=${start_date}&end_date=${end_date}`).catch(err => {
          console.error('❌ BILLING: Error en /orders/summary completed:', err.message);
          return { data: {} };
        }),
        wpApi.get(`/orders/summary?status=pending&start_date=${start_date}&end_date=${end_date}`).catch(err => {
          console.error('❌ BILLING: Error en /orders/summary pending:', err.message);
          return { data: {} };
        })
      ]);

      console.log('📊 BILLING: Respuestas de endpoints optimizados:', {
        completedSummaryKeys: Object.keys(completedSummaryResponse.data || {}),
        pendingSummaryKeys: Object.keys(pendingSummaryResponse.data || {})
      });
      
      // Usar summary como fuente principal de datos (más preciso que /orders/status)
      const completedSummary = completedSummaryResponse.data || {};
      const pendingSummary = pendingSummaryResponse.data || {};
      
      // Calcular totales del período sumando todas las semanas
      let totalCompleted = 0;
      let totalCompletedOrders = 0;
      let totalPending = 0;
      let totalPendingOrders = 0;
      
      // Procesar summary de completed
      Object.values(completedSummary).forEach(week => {
        if (typeof week === 'object' && week !== null) {
          if (week.total_amount) totalCompleted += parseFloat(week.total_amount);
          if (week.total_orders) totalCompletedOrders += parseInt(week.total_orders);
        }
      });
      
      // Procesar summary de pending
      Object.values(pendingSummary).forEach(week => {
        if (typeof week === 'object' && week !== null) {
          if (week.total_amount) totalPending += parseFloat(week.total_amount);
          if (week.total_orders) totalPendingOrders += parseInt(week.total_orders);
        }
      });

      console.log('📅 BILLING: Totales calculados desde summary:', {
        totalCompleted,
        totalCompletedOrders,
        totalPending,
        totalPendingOrders
      });

      // Usar totales calculados desde summary (más eficiente y preciso)
      const totalCollected = totalCompleted;
      const totalOrders = totalCompletedOrders;
      const pendingAmount = totalPending;
      const pendingOrders = totalPendingOrders;

      // Obtener pagos atrasados usando función existente
      console.log('⏰ BILLING: Obteniendo pagos atrasados...');
      const overdueData = await getOverduePayments(30);

      const finalStats = {
        total_collected: totalCollected,
        total_orders: totalOrders,
        pending_amount: pendingAmount,
        pending_orders: pendingOrders,
        overdue_amount: overdueData.data.reduce((sum, order) => sum + parseFloat(order.amount_due || 0), 0),
        overdue_orders: overdueData.total,
        monthly_stats: [],
        period: { start_date, end_date }
      };

      console.log('✅ BILLING: Estadísticas finales calculadas:', finalStats);

      return finalStats;

    } catch (endpointError) {
      console.error('❌ BILLING: Plugin endpoints error, usando fallback:', endpointError);
      
      // Verificar si hay datos parciales
      let hasPartialData = false;
      let fallbackStats = {
        total_collected: 0,
        total_orders: 0,
        pending_amount: 0,
        pending_orders: 0,
        overdue_amount: 0,
        overdue_orders: 0,
        monthly_stats: [],
        period: { start_date, end_date }
      };

      // Intentar obtener al menos estadísticas básicas usando summary endpoints
      try {
        console.log('🔄 BILLING: Intentando fallback con summary endpoints...');
        const [basicCompletedResponse, basicPendingResponse] = await Promise.allSettled([
          wpApi.get(`/orders/summary?status=completed`),
          wpApi.get(`/orders/summary?status=pending`)
        ]);
        
        if (basicCompletedResponse.status === 'fulfilled' && basicCompletedResponse.value.data) {
          const completedData = basicCompletedResponse.value.data;
          let totalCompleted = 0;
          let totalOrders = 0;
          
          Object.values(completedData).forEach(week => {
            if (typeof week === 'object' && week !== null) {
              if (week.total_amount) totalCompleted += parseFloat(week.total_amount);
              if (week.total_orders) totalOrders += parseInt(week.total_orders);
            }
          });
          
          fallbackStats.total_collected = totalCompleted;
          fallbackStats.total_orders = totalOrders;
          hasPartialData = true;
        }
        
        if (basicPendingResponse.status === 'fulfilled' && basicPendingResponse.value.data) {
          const pendingData = basicPendingResponse.value.data;
          let totalPending = 0;
          let totalPendingOrders = 0;
          
          Object.values(pendingData).forEach(week => {
            if (typeof week === 'object' && week !== null) {
              if (week.total_amount) totalPending += parseFloat(week.total_amount);
              if (week.total_orders) totalPendingOrders += parseInt(week.total_orders);
            }
          });
          
          fallbackStats.pending_amount = totalPending;
          fallbackStats.pending_orders = totalPendingOrders;
          hasPartialData = true;
        }
        
        if (hasPartialData) {
          console.log('✅ BILLING: Datos básicos obtenidos del fallback optimizado');
        }
      } catch (fallbackError) {
        console.error('❌ BILLING: Fallback optimizado también falló:', fallbackError.message);
      }

      if (!hasPartialData) {
        console.log('⚠️ BILLING: Usando datos simulados de emergencia');
        fallbackStats = {
          total_collected: 2450000,
          total_orders: 70,
          pending_amount: 875000,
          pending_orders: 25,
          overdue_amount: 420000,
          overdue_orders: 12,
          monthly_stats: [],
          period: { start_date, end_date }
        };
      }

      return fallbackStats;
    }

  } catch (error) {
    console.error('❌ BILLING: Error crítico fetching payment stats:', error);
    
    // Retornar estadísticas por defecto en caso de error crítico
    return {
      total_collected: 0,
      total_orders: 0,
      pending_amount: 0,
      pending_orders: 0,
      overdue_amount: 0,
      overdue_orders: 0,
      monthly_stats: [],
      period: dateRange,
      error: 'Unable to fetch payment statistics',
      error_details: error.message
    };
  }
};

/**
 * Exportar pagos a diferentes formatos
 * @param {Object} filters - Filtros aplicados
 * @param {string} format - Formato de exportación ('csv', 'excel', 'pdf')
 * @returns {Promise} Datos para exportación
 */
export const exportPayments = async (filters = {}, format = 'csv') => {
  try {
    console.log('Starting export with filters:', filters); // Debug
    
    let allOrders = [];
    
    // Si hay IDs seleccionadas, obtener solo esas órdenes
    if (filters.selected_order_ids && filters.selected_order_ids.length > 0) {
      console.log('Exporting selected orders:', filters.selected_order_ids.length);
      
      // Obtener cada orden seleccionada individualmente
      const wooApi = createWooCommerceInstance();
      const selectedOrders = [];
      
      for (const orderId of filters.selected_order_ids) {
        try {
          const response = await wooApi.get(`orders/${orderId}`);
          if (response.data) {
            selectedOrders.push(response.data);
          }
        } catch (error) {
          console.warn(`Failed to get order ${orderId}:`, error.message);
        }
      }
      
      allOrders = selectedOrders;
    } else {
      // Comportamiento normal: usar filtros para obtener todos los datos
      const response = await getPaymentsWithFilters(filters, { page: 1, per_page: -1 });
      allOrders = response.data;
    }

    console.log(`Export: processing ${allOrders.length} orders for ${format} format`); // Debug

    // Preparar datos para exportación con estructura real
    const exportData = allOrders.map(order => {
      const customerData = order.customer_info || order.billing || {};
      const customerName = customerData.first_name && customerData.last_name
        ? `${customerData.first_name} ${customerData.last_name}`.trim()
        : customerData.username || 'Sin nombre';

      // Determinar medio de pago: si no es Webpay, es presencial
      const getPaymentMethod = () => {
        // Primero verificar si tiene payment_method_title
        if (order.payment_method_title) {
          // Si contiene "Webpay", es Webpay
          if (order.payment_method_title.toLowerCase().includes('webpay')) {
            return 'Webpay';
          }
          // Si no es Webpay, retornar el título del método tal como está
          return order.payment_method_title;
        }
        
        // Verificar en meta_data si hay información de transacción de Webpay
        const hasWebpayTransaction = order.meta_data?.some(meta => 
          meta.key === 'webpay_transaction_id' || 
          meta.key === 'transactionStatus' ||
          meta.key === 'paymentType'
        );
        
        if (hasWebpayTransaction) {
          return 'Webpay';
        }
        
        // Si no hay indicadores de Webpay, es presencial
        return 'Presencial';
      };

      return {
        id: order.id,
        fecha_pago: formatChileDate(order.date_paid),
        nombre: customerName,
        email: customerData.email || order.billing?.email || 'Sin email',
        monto: order.total,
        estado: order.status,
        metodo_pago: getPaymentMethod(),
        concepto: order.meta_data?.find(meta => meta.key === 'concepto')?.value || 'N/A',
        mes: order.meta_data?.find(meta => meta.key === 'mes')?.value || 'N/A',
        grupo: order.meta_data?.find(meta => meta.key === 'customer_group_name')?.value ||
               order.meta_data?.find(meta => meta.key === 'grupo')?.value || 'N/A',
        numero_boleta: order.meta_data?.find(meta => meta.key === 'billmysales_invoice_number')?.value || ''
      };
    });

    console.log(`Export: prepared ${exportData.length} records for export`); // Debug

    return {
      data: exportData,
      total: exportData.length,
      format,
      filters_applied: filters,
      generated_at: new Date().toISOString()
    };

  } catch (error) {
    console.error('Error exporting payments:', error);
    throw error;
  }
};

/**
 * Obtener un cliente por ID
 * Usa WooCommerce API como primera opción, fallback a búsqueda personalizada
 * @param {number} customerId - ID del cliente
 * @returns {Promise} Datos del cliente o null
 */
export const getCustomerById = async (customerId) => {
  if (!customerId) return null;

  try {
    // Intentar con WooCommerce API primero
    const wooApi = createWooCommerceInstance();
    try {
      const response = await wooApi.get(`customers/${customerId}`);
      if (response.data) {
        return {
          id: response.data.id,
          name: `${response.data.first_name} ${response.data.last_name}`.trim(),
          email: response.data.email,
          display_text: `${response.data.first_name} ${response.data.last_name} (${response.data.email})`.trim()
        };
      }
    } catch (wooError) {
      console.log('WooCommerce API failed, trying fallback search');
    }

    // Fallback: usar servicio unificado para buscar por el ID
    const searchResults = await unifiedSearchCustomers(customerId.toString(), 1);
    
    if (searchResults.length > 0) {
      const customer = searchResults[0];
      if (customer.id == customerId) {
        return customer;
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting customer by ID:', error);
    return null;
  }
};

/**
 * Buscar clientes usando el servicio unificado
 * @param {string} searchTerm - Término de búsqueda
 * @param {number} limit - Límite de resultados
 * @returns {Promise} Lista de clientes
 */
export const searchCustomers = async (searchTerm, limit = 10) => {
  // Delegar completamente al servicio unificado
  return await unifiedSearchCustomers(searchTerm, limit);
};

/**
 * Obtener últimos pagos
 * @param {number} limit - Límite de resultados
 * @returns {Promise} Últimos pagos
 */
export const getRecentPayments = async (limit = 10) => {
  try {
    const wpApi = createWordPressAPIInstance();
    const response = await wpApi.get('/last-orders');
    
    return response.data.slice(0, limit);
  } catch (error) {
    console.error('Error fetching recent payments:', error);
    return [];
  }
};


/**
 * Formatear monto en pesos chilenos
 * @param {number} amount - Monto a formatear
 * @returns {string} Monto formateado
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Obtener configuración completa desde localStorage (filtros + paginación)
 * @returns {Object} Configuración guardada
 */
export const getSavedBillingConfig = () => {
  try {
    const saved = localStorage.getItem('billing-config');
    return saved ? JSON.parse(saved) : {
      filters: getDefaultFilters(),
      pagination: { page: 1, per_page: 50 },
      timestamp: Date.now()
    };
  } catch (error) {
    return {
      filters: getDefaultFilters(),
      pagination: { page: 1, per_page: 50 },
      timestamp: Date.now()
    };
  }
};

/**
 * Guardar configuración completa en localStorage
 * @param {Object} config - Configuración completa
 */
export const saveBillingConfig = (config) => {
  try {
    localStorage.setItem('billing-config', JSON.stringify({
      ...config,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error saving billing config:', error);
  }
};

/**
 * Obtener configuración de filtros desde localStorage
 * @returns {Object} Filtros guardados
 */
export const getSavedFilters = () => {
  return getSavedBillingConfig().filters;
};

/**
 * Guardar filtros en localStorage
 * @param {Object} filters - Filtros a guardar
 */
export const saveFilters = (filters) => {
  const config = getSavedBillingConfig();
  saveBillingConfig({ ...config, filters });
};

/**
 * Obtener filtros por defecto (año actual)
 * @returns {Object} Filtros por defecto
 */
export const getDefaultFilters = () => {
  const currentYear = new Date().getFullYear();
  return {
    start_date: `${currentYear}-01-01`,
    end_date: `${currentYear}-12-31`,
    status: 'all',
    customer_id: '',
    order_id: '',
    group: '',
    min_amount: '',
    max_amount: ''
  };
};

// ========================================
// SINCRONIZACIÓN DE CUSTOMER INFO A ÓRDENES
// ========================================

/**
 * Sincronizar información del customer a las órdenes
 * Copia meta_data actualizado (direccion, comuna, apoderado_*, etc.) y genera glosa
 *
 * @param {Object} options - Opciones de sincronización
 * @param {number} options.limit - Número de órdenes a procesar (default: 100)
 * @param {string} options.status - Estados de órdenes separados por coma (default: 'pending,completed')
 * @param {string} options.desde - Fecha desde (format: YYYY-MM-DD, default: año actual)
 * @param {boolean} options.force_update - Forzar actualización incluso si ya tiene info (default: false)
 * @returns {Promise<Object>} Resultado de la sincronización
 */
export const syncCustomerToOrders = async (options = {}) => {
  try {
    const wpApi = createWordPressAPIInstance();

    const params = {
      limit: options.limit || 100,
      status: options.status || 'pending',
      desde: options.desde || `${new Date().getFullYear()}-01-01`,
      force_update: options.force_update || false
    };

    console.log('🔄 SYNC: Iniciando sincronización con parámetros:', params);

    const response = await wpApi.post('/migrate-customer-info', params);

    console.log('✅ SYNC: Sincronización completada:', response.data);

    return {
      success: true,
      data: response.data,
      message: `${response.data.processed || 0} órdenes sincronizadas correctamente`
    };

  } catch (error) {
    console.error('❌ SYNC: Error en sincronización:', error);

    return {
      success: false,
      error: error.response?.data || error.message,
      message: 'Error al sincronizar información de órdenes'
    };
  }
};

/**
 * Sincronizar información en lotes grandes (optimizado)
 * Usa paginación para procesar miles de órdenes
 *
 * @param {Object} options - Opciones de sincronización batch
 * @param {number} options.batch_size - Tamaño del lote (default: 50)
 * @param {number} options.offset - Desde qué orden empezar (default: 0)
 * @param {string} options.status - Estados de órdenes (default: 'pending,completed')
 * @param {string} options.desde - Fecha desde (default: año actual)
 * @param {boolean} options.force_update - Forzar actualización (default: false)
 * @returns {Promise<Object>} Resultado del lote procesado
 */
export const syncCustomerToOrdersBatch = async (options = {}) => {
  try {
    const wpApi = createWordPressAPIInstance();

    const params = {
      batch_size: options.batch_size || 50,
      offset: options.offset || 0,
      status: options.status || 'pending',
      desde: options.desde || `${new Date().getFullYear()}-01-01`,
      force_update: options.force_update || false
    };

    console.log('🔄 SYNC BATCH: Procesando lote:', params);

    const response = await wpApi.post('/migrate-batch', params);

    console.log('✅ SYNC BATCH: Lote completado:', response.data);

    return {
      success: true,
      data: response.data,
      hasMore: response.data.batch_info?.has_more || false,
      message: `Lote procesado: ${response.data.processed || 0} órdenes`
    };

  } catch (error) {
    console.error('❌ SYNC BATCH: Error en lote:', error);

    return {
      success: false,
      error: error.response?.data || error.message,
      message: 'Error al procesar lote de sincronización'
    };
  }
};

/**
 * Sincronizar órdenes específicas por sus IDs
 * Usa EXACTAMENTE las órdenes proporcionadas (las que se ven en la vista filtrada)
 *
 * @param {Array<number>} orderIds - Array de IDs de órdenes a sincronizar
 * @returns {Promise<Object>} Resultado de la sincronización
 */
export const syncOrdersByIds = async (orderIds) => {
  try {
    const wpApi = createWordPressAPIInstance();

    if (!orderIds || orderIds.length === 0) {
      return {
        success: false,
        message: 'No se proporcionaron órdenes para sincronizar'
      };
    }

    console.log('🔄 SYNC IDS: Sincronizando órdenes específicas:', orderIds.length);

    const response = await wpApi.post('/sync-orders-by-ids', {
      order_ids: orderIds
    });

    console.log('✅ SYNC IDS: Completado:', response.data);

    return {
      success: true,
      data: response.data,
      message: response.data.message || `${response.data.processed} órdenes sincronizadas`
    };

  } catch (error) {
    console.error('❌ SYNC IDS: Error:', error);

    return {
      success: false,
      error: error.response?.data || error.message,
      message: 'Error al sincronizar órdenes específicas'
    };
  }
};

/**
 * Re-sincronizar una orden específica
 * Fuerza la actualización de customer_info y glosa en los line_items
 *
 * @param {number} orderId - ID de la orden a re-sincronizar
 * @returns {Promise<Object>} Resultado de la re-sincronización
 */
export const resyncSingleOrder = async (orderId) => {
  try {
    const wpApi = createWordPressAPIInstance();

    if (!orderId) {
      return {
        success: false,
        message: 'ID de orden requerido'
      };
    }

    console.log('🔄 RESYNC: Re-sincronizando orden:', orderId);

    const response = await wpApi.post(`/resync-order/${orderId}`);

    console.log('✅ RESYNC: Completado:', response.data);

    return {
      success: true,
      data: response.data,
      message: response.data.message || `Orden #${orderId} re-sincronizada exitosamente`
    };

  } catch (error) {
    console.error('❌ RESYNC: Error:', error);

    return {
      success: false,
      error: error.response?.data || error.message,
      message: `Error al re-sincronizar orden #${orderId}`
    };
  }
};

// ========================================
// ACTUALIZACIÓN MASIVA DE NÚMEROS DE BOLETA
// ========================================

/**
 * Actualizar números de boleta masivamente
 * Busca órdenes con billmysales_order_id y actualiza el billmysales_invoice_number
 * 
 * @param {Object} options - Opciones de actualización
 * @param {string} options.start_date - Fecha de inicio (YYYY-MM-DD)
 * @param {string} options.end_date - Fecha de fin (YYYY-MM-DD)
 * @param {string} options.status - Estado de órdenes (default: 'completed')
 * @param {number} options.limit - Límite de órdenes por lote (default: 50)
 * @param {boolean} options.force_update - Forzar actualización incluso si ya tienen número (default: false)
 * @returns {Promise<Object>} Resultado de la actualización masiva
 */
export const bulkUpdateInvoiceNumbers = async (options = {}) => {
  try {
    const wpApi = createWordPressAPIInstance();

    const params = {
      start_date: options.start_date,
      end_date: options.end_date,
      status: options.status || 'completed',
      limit: options.limit || 50,
      offset: options.offset || 0,
      force_update: options.force_update || false
    };

    // Modo chunked (paginación por lotes desde el frontend con progreso visual)
    if (options.chunked_mode) {
      params.chunked_mode = true;
    }

    // Si se proporcionan IDs específicos, agregarlos
    if (options.order_ids && options.order_ids.length > 0) {
      params.order_ids = options.order_ids.join(',');
    }

    if (!params.start_date || !params.end_date) {
      return {
        success: false,
        message: 'start_date y end_date son requeridos'
      };
    }

    console.log('🔄 BULK UPDATE INVOICE: Iniciando actualización masiva con parámetros:', params);

    const response = await wpApi.post('/orders/bulk-update-invoice-numbers', params);

    console.log('✅ BULK UPDATE INVOICE: Completado:', response.data);

    return {
      success: true,
      data: response.data,
      message: response.data.message || 'Actualización masiva completada'
    };

  } catch (error) {
    console.error('❌ BULK UPDATE INVOICE: Error:', error);

    return {
      success: false,
      error: error.response?.data || error.message,
      message: 'Error en la actualización masiva de números de boleta'
    };
  }
};

/**
 * Actualizar números de boleta para órdenes específicas por sus IDs
 * Usa EXACTAMENTE las órdenes proporcionadas (las que se ven en la vista filtrada)
 * 
 * @param {Array<number>} orderIds - Array de IDs de órdenes a actualizar
 * @param {boolean} forceUpdate - Forzar actualización incluso si ya tienen número
 * @returns {Promise<Object>} Resultado de la actualización
 */
export const updateInvoiceNumbersByIds = async (orderIds, forceUpdate = false) => {
  try {
    const wpApi = createWordPressAPIInstance();

    if (!orderIds || orderIds.length === 0) {
      return {
        success: false,
        message: 'No se proporcionaron órdenes para actualizar'
      };
    }

    console.log('🔄 UPDATE INVOICE IDS: Actualizando números de boleta para órdenes específicas:', orderIds.length);

    // Procesar en lotes para evitar timeouts
    const batchSize = 10;
    let allResults = {
      processed: 0,
      updated: 0,
      skipped: 0,
      errors: 0,
      details: []
    };

    for (let i = 0; i < orderIds.length; i += batchSize) {
      const batch = orderIds.slice(i, i + batchSize);
      
      console.log(`📦 UPDATE INVOICE: Procesando lote ${Math.floor(i/batchSize) + 1} - ${batch.length} órdenes`);

      // Procesar cada orden del lote individualmente usando el endpoint existente
      for (const orderId of batch) {
        try {
          const response = await wpApi.get(`/orders/${orderId}/billmysales-pdf`);
          
          if (response.data.success) {
            allResults.processed++;
            
            if (response.data.invoice_number_saved) {
              allResults.updated++;
              allResults.details.push({
                order_id: orderId,
                status: 'updated',
                invoice_number: response.data.invoice_number,
                message: 'Número de boleta actualizado'
              });
            } else {
              allResults.skipped++;
              allResults.details.push({
                order_id: orderId,
                status: 'skipped',
                invoice_number: response.data.invoice_number,
                message: response.data.message_extra || 'Ya tenía número de boleta'
              });
            }
          } else {
            allResults.errors++;
            allResults.details.push({
              order_id: orderId,
              status: 'error',
              message: response.data.message || 'Sin BillMySales ID'
            });
          }
        } catch (error) {
          allResults.errors++;
          allResults.processed++;
          allResults.details.push({
            order_id: orderId,
            status: 'error',
            message: error.response?.data?.message || error.message
          });
        }

        // Pequeña pausa para no saturar la API
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    console.log('✅ UPDATE INVOICE IDS: Proceso completado:', allResults);

    return {
      success: allResults.errors === 0,
      data: { results: allResults },
      message: `Proceso completado: ${allResults.updated} números de boleta actualizados de ${allResults.processed} órdenes procesadas`
    };

  } catch (error) {
    console.error('❌ UPDATE INVOICE IDS: Error:', error);

    return {
      success: false,
      error: error.response?.data || error.message,
      message: 'Error al actualizar números de boleta por IDs'
    };
  }
};

/**
 * Sincronizar órdenes por lote usando trigger_order_status_change
 * Esto garantiza que todos los hooks de WooCommerce se ejecuten para cada orden
 * Procesa las órdenes con su estado actual para disparar webhooks y sincronización
 *
 * @param {Array<number>} orderIds - Array de IDs de órdenes
 * @param {Object} options - Opciones de configuración
 * @returns {Promise<Object>} Resultado de la sincronización por lote
 */
export const triggerStatusChangeBatch = async (orderIds, options = {}) => {
  try {
    const { 
      batchSize = 25, 
      delayMs = 500,
      progressCallback = null 
    } = options;

    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      return {
        success: false,
        message: 'Array de IDs de órdenes requerido'
      };
    }

    console.log(`🔄 TRIGGER BATCH: Sincronizando ${orderIds.length} órdenes con hooks nativos...`);

    let processed = 0;
    let successful = 0;
    let errors = [];
    const results = [];

    // Procesar en lotes para evitar sobrecarga
    const batches = [];
    for (let i = 0; i < orderIds.length; i += batchSize) {
      batches.push(orderIds.slice(i, i + batchSize));
    }

    console.log(`📦 TRIGGER BATCH: ${batches.length} lotes de máximo ${batchSize} órdenes`);

    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      console.log(`🔄 Procesando lote ${batchIndex + 1}/${batches.length} - ${batch.length} órdenes`);

      // Primero obtener el estado actual de cada orden del lote
      const batchOrderDetails = await Promise.all(
        batch.map(async (orderId) => {
          try {
            const orderDetail = await getOrderDetails(orderId);
            return { 
              orderId, 
              currentStatus: orderDetail.data?.status || 'pending' 
            };
          } catch (error) {
            console.error(`❌ Error obteniendo estado de orden #${orderId}:`, error);
            return { orderId, currentStatus: 'pending' }; // fallback
          }
        })
      );

      // Procesar cada orden del lote con su estado actual
      for (const orderInfo of batchOrderDetails) {
        try {
          const result = await triggerStatusChange(orderInfo.orderId, orderInfo.currentStatus);
          
          if (result.success) {
            successful++;
            console.log(`✅ Orden #${orderInfo.orderId}: Hooks disparados exitosamente`);
          } else {
            errors.push(`Orden #${orderInfo.orderId}: ${result.message}`);
            console.error(`❌ Orden #${orderInfo.orderId}: ${result.message}`);
          }
          
          results.push(result);
          processed++;

          // Callback de progreso si se proporciona
          if (progressCallback) {
            progressCallback(processed, orderIds.length, successful, errors.length);
          }

          // Delay entre órdenes para no saturar el servidor
          if (delayMs > 0 && processed < orderIds.length) {
            await new Promise(resolve => setTimeout(resolve, delayMs));
          }

        } catch (error) {
          processed++;
          const errorMsg = `Excepción en orden #${orderInfo.orderId}: ${error.message}`;
          errors.push(errorMsg);
          console.error('❌', errorMsg);
          
          results.push({
            success: false,
            orderId: orderInfo.orderId,
            error: error.message
          });
        }
      }

      // Pausa entre lotes si no es el último
      if (batchIndex < batches.length - 1) {
        console.log(`⏳ Pausa de ${delayMs}ms entre lotes...`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }

    const finalResult = {
      success: errors.length === 0,
      processed_orders: processed,
      successful_orders: successful,
      error_count: errors.length,
      errors: errors.slice(0, 10), // Solo primeros 10 errores
      message: `Sincronización por lote completada: ${successful}/${processed} órdenes exitosas`,
      results: results
    };

    console.log('📊 TRIGGER BATCH: Resultado final:', finalResult);
    return finalResult;

  } catch (error) {
    console.error('❌ TRIGGER BATCH: Error general:', error);
    return {
      success: false,
      error: error.message,
      message: 'Error durante sincronización por lote con hooks nativos'
    };
  }
};

/**
 * Cambiar estado de una orden usando el sistema interno de WooCommerce
 * Esto DISPARA los hooks nativos (woocommerce_order_status_changed)
 * Equivalente a "Acciones por lote > Cambiar estado" en el admin de WordPress
 *
 * @param {number} orderId - ID de la orden
 * @param {string} status - Estado nuevo (ej: "completed", "pending")
 * @returns {Promise<Object>} Resultado del cambio de estado
 */
export const triggerStatusChange = async (orderId, status) => {
  try {
    const wpApi = createWordPressAPIInstance();

    if (!orderId) {
      return {
        success: false,
        message: 'ID de orden requerido'
      };
    }

    if (!status) {
      return {
        success: false,
        message: 'Estado requerido'
      };
    }

    console.log(`🔄 TRIGGER STATUS: Cambiando orden #${orderId} a estado "${status}"`);

    const response = await wpApi.post(`/trigger-status-change/${orderId}`, {
      status: status
    });

    console.log('✅ TRIGGER STATUS: Completado:', response.data);

    return {
      success: true,
      data: response.data,
      message: response.data.message || `Orden #${orderId} actualizada a estado "${status}"`
    };

  } catch (error) {
    console.error('❌ TRIGGER STATUS: Error:', error);

    return {
      success: false,
      error: error.response?.data || error.message,
      message: `Error al cambiar estado de orden #${orderId}`
    };
  }
};